import { Box, Button, Chip, Stack, Tooltip } from "@mui/material";
import React, { useMemo } from "react";
import { useAppDispatch, useTypedSelector } from "@/config/configureAppStore";
import {
  setDestinationLanguage,
  setLipSync,
  setQualityCheck
} from "@/reducers/slices/clientProjectPopupSlice/clientProjectPopupSlice";
import { SwitchCard } from "@/components/elements/SwitchCard";
import NonQCLarge from "@/components/assets/NonQCLarge";
import QualityCheckLarge from "@/components/assets/QualityCheckLarge";
import { ChevronRightRounded, LockRounded } from "@mui/icons-material";
import { PLANS_CATEGORY } from "@/features/pricing/types";
import useCreateProject from "@/features/createProject/hooks/useCreateProject";
import { PROJECT_FLOW_TYPES } from "@/features/createProject/constants";
import useContactSales from "@/hooks/useContactSales";

const AutomatedDubDisabledTooltip = () => (
  <Tooltip
    title={"Automated Dubbing has been disabled in your workspace by the admin"}
  >
    <Chip
      variant="filled"
      color="warning"
      label={
        <LockRounded
          sx={{
            height: 16,
            width: 16
          }}
        />
      }
      size="small"
      minimal
      sx={{
        position: "absolute",
        top: 4,
        right: 4,
        "& .MuiChip-label": {
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }
      }}
    />
  </Tooltip>
);

export const RequirementsServiceSelector = ({
  minimal = false
}: {
  minimal?: boolean;
}): React.ReactNode => {
  const dispatch = useAppDispatch();
  const { currentFlowType } = useCreateProject();
  const isLipSyncSelected = useTypedSelector(
    (state) => state.clientProjectPopup.lipSyncEnabled
  );
  const isQualityCheckSelected = useTypedSelector(
    (state) => state.clientProjectPopup.qualityCheckEnabled
  );
  const targetLocales = useTypedSelector(
    (state) => state.clientProjectPopup.targetLocales
  );
  const supportedNonQCTargetLocales = useTypedSelector(
    (state) =>
      state.user?.activeWorkspaceData?.supportedNonQCTargetLocales?.map(
        (locale) => locale.locale
      ) || []
  );
  const supportedQCTargetLocales = useTypedSelector(
    (state) =>
      state.user?.activeWorkspaceData?.supportedQCTargetLocales?.map(
        (locale) => locale.locale
      ) || []
  );
  // const isLipSyncAllowed = useTypedSelector(
  //   (state) => state.user.activeWorkspaceData?.lipSyncEnabled
  // );
  const isQCAllowed = useTypedSelector(
    (state) => state.user.activeWorkspaceData?.qcEnabled
  );
  const isAutomatedDubAllowed = useTypedSelector(
    (state) => !state.user.activeWorkspaceData?.disableAutomatedDub
  );

  const workspacePlan = useTypedSelector(
    (state) => state.workspace?.workspacePlan?.planDetails
  );

  // const invertLipSync = () => {
  //   const newState = !isLipSyncSelected;
  //   if (newState && isQCAllowed) {
  //     dispatch(setQualityCheck(true));
  //   }
  //   dispatch(setLipSync(newState));
  // };

  const setQualityCheckSelection = (state: boolean) => {
    if (state === isQualityCheckSelected) {
      dispatch(setQualityCheck(null));
    } else {
      if (!state && isLipSyncSelected) {
        dispatch(setLipSync(false));
      }
      dispatch(setQualityCheck(state));
      let updatedTargetLocales = targetLocales;
      if (state) {
        updatedTargetLocales = targetLocales.filter((locale) =>
          supportedQCTargetLocales.includes(locale)
        );
      } else {
        updatedTargetLocales = targetLocales.filter((locale) =>
          supportedNonQCTargetLocales.includes(locale)
        );
      }
      dispatch(setDestinationLanguage(updatedTargetLocales));
    }
  };

  const qcDisabledReason = useMemo(() => {
    if (isQCAllowed) {
      return "";
    }
    if (workspacePlan?.category !== PLANS_CATEGORY.CUSTOM) {
      return "Enterprise only";
    }
    return "Coming soon";
  }, [isQCAllowed, workspacePlan]);

  const { onContactSales } = useContactSales();

  return (
    <Stack direction={"row"} gap={minimal ? 7 : 2}>
      <SwitchCard
        clickId="automated-dubbing"
        title="Automated Dubbing"
        icon={<NonQCLarge />}
        caption={"Fast dubbing delivery with AI translations"}
        isSelected={isQualityCheckSelected === false}
        onClick={() => {
          setQualityCheckSelection(false);
        }}
        size={minimal ? "small" : "large"}
        noOuterStyle={!minimal}
        disabled={!isAutomatedDubAllowed}
        postComponent={
          isAutomatedDubAllowed ? null : <AutomatedDubDisabledTooltip />
        }
      />
      <SwitchCard
        clickId="quality-check"
        title="Quality Assurance"
        icon={<QualityCheckLarge />}
        caption={"In-house experts ensure precise translations and voiceover"}
        isSelected={isQualityCheckSelected}
        onClick={() => {
          setQualityCheckSelection(true);
        }}
        size={minimal ? "small" : "large"}
        disabled={!isQCAllowed}
        noOuterStyle={!minimal}
        postTitleComponent={
          !isQCAllowed && minimal ? (
            <Chip
              variant="filled"
              color="success"
              label={qcDisabledReason}
              size="small"
              minimal
            />
          ) : null
        }
        postComponent={
          isQCAllowed ? null : minimal ? null : (
            <Box>
              <Button
                size="medium"
                variant="contained"
                fullWidth={false}
                color={
                  currentFlowType === PROJECT_FLOW_TYPES.ICP
                    ? "primary"
                    : "secondary"
                }
                endIcon={<ChevronRightRounded />}
                onClick={() => {
                  onContactSales("Create Project - QA");
                }}
              >
                Contact Sales
              </Button>
            </Box>
          )
        }
      />
    </Stack>
  );
};
